import type { AgentView } from "@walkie/protocol"
import { memo, useEffect, useState } from "react"
import { api } from "../api/client"
import {
	agentColor,
	APP_LABEL,
	APP_SHORT,
	AUTO_RESUME_ICON,
	HANDLE_RE,
	WAKE_ICON,
	WAKE_INFO,
} from "../lib/agents"
import { age, dur } from "../lib/time"
import { renameAgent, setAutoResume, setShared } from "../store/actions"
import { useBlockedAgents, useFailingAgents, useSortedAgents } from "../store/derived"
import { setUi, useStore } from "../store/store"
import { chatLink } from "./ConnectPanel"
import { StateDot, Swatch, Tip } from "./ui"

export function AgentsList() {
	const agents = useSortedAgents()
	const blocked = useBlockedAgents()
	const failing = useFailingAgents()
	if (!agents.length) return <NoAgents />
	return (
		<div className="agents" role="list" aria-label="Agents">
			{agents.map(a => (
				<AgentCard
					key={a.id}
					a={a}
					blocked={blocked.has(a.id)}
					failing={failing.get(a.id) ?? 0}
				/>
			))}
		</div>
	)
}

/**
 * Before the first chat joins. Connecting installs hooks; a chat only shows up
 * once it starts, or (if already open) takes its next turn — so say that,
 * rather than asking to connect again.
 */
function NoAgents() {
	const projectId = useStore(s => s.projectId)
	const [conn, setConn] = useState<
		{ device: string; path: string | null; apps: string[] } | null | undefined
	>(undefined)
	useEffect(() => {
		let off = false
		api
			.devices()
			.then(r => {
				if (off) return
				for (const d of r.devices)
					for (const c of d.connections)
						if (c.project_id === projectId)
							return setConn({ device: d.name, path: c.paths[0] ?? null, apps: c.apps })
				setConn(null)
			})
			.catch(() => !off && setConn(null))
		return () => {
			off = true
		}
	}, [projectId])
	if (conn === undefined) return <div className="empty left">…</div>
	if (!conn)
		return (
			<div className="empty left">
				No agent chats yet.{" "}
				<button className="btn btn-xs" onClick={() => setUi({ connect: true })}>
					Connect Claude or Codex
				</button>
			</div>
		)
	const folder = conn.path?.split("/").pop() ?? "the repo"
	return (
		<div className="empty left no-agents">
			<div>
				<span className="ok">✓ Connected</span> on {conn.device}. Waiting for a chat:
				start one in <b className="mono">{folder}</b>, or send a message in a chat that's
				already open there.
			</div>
			<div className="row" style={{ gap: 6, marginTop: 6 }}>
				{conn.apps.includes("claude-code") && (
					<a className="btn btn-xs" href={chatLink("claude-code", conn.path)}>
						New Claude chat
					</a>
				)}
				{conn.apps.some(a => a.startsWith("codex")) && (
					<a className="btn btn-xs" href={chatLink("codex", conn.path)}>
						New Codex chat
					</a>
				)}
			</div>
		</div>
	)
}

const AUTO_RESUME_TIP =
	"When idle and not open in an app, Walkie resumes this Codex CLI thread headlessly to answer. Runs with the thread's own Codex permissions."
const DESKTOP_RESUME_TIP =
	"Desktop threads cannot be resumed while the app holds them; messages arrive at the next prompt."

const AgentCard = memo(function AgentCard({
	a,
	blocked,
	failing,
}: {
	a: AgentView
	blocked: boolean
	failing: number
}) {
	const meId = useStore(s => s.me?.user.id)
	const now = useStore(s => s.now)
	const flash = useStore(s => s.ui.focusAgentId === a.id)
	const parent = useStore(s =>
		a.forkOf ? s.agents[a.forkOf] : a.parentId ? s.agents[a.parentId] : undefined,
	)
	const authorFilter = useStore(s => s.ui.authorFilter)
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(a.handle)
	const mine = a.userId === meId
	const codex = a.app.startsWith("codex")
	const desktop = a.app === "codex-desktop"
	/** Owner can flip auto-resume here (disabled for Desktop, which holds its threads). */
	const showResume = mine && codex
	const wake = WAKE_INFO[a.presentation]
	const capName = WAKE_ICON[a.presentation].name
	// A wake-capable chat is wakeable this moment only while its waiter runs.
	const disarmed =
		a.presentation === "hook-rewake" &&
		a.wakeArmed === false &&
		a.state !== "active" &&
		a.state !== "ended"
	const capTip = disarmed
		? "Can wake when idle, but its waiter isn't running right now (the chat was paused or the connector restarted). It will see messages at its next turn, and can be woken again after that."
		: wake.tip.startsWith(capName)
			? wake.tip
			: `${capName}: ${wake.tip}`
	const pending = a.stats?.pending ?? 0
	const provider =
		a.provider === "openai" ? "openai" : a.provider === "anthropic" ? "anthropic" : ""
	const cls = [
		"agent",
		"st-" + a.state,
		!a.shared && "private",
		failing > 0 && "failing",
		blocked && "blocked",
		flash && "flash",
	]
		.filter(Boolean)
		.join(" ")

	const commit = async () => {
		const h = draft.trim().toLowerCase().replace(/^@/, "")
		if (h && h !== a.handle && HANDLE_RE.test(h)) {
			if (!(await renameAgent(a.id, h))) return
		}
		setEditing(false)
	}

	const summary = [
		`@${a.handle}`,
		`${a.state}`,
		`${APP_LABEL[a.app]} on ${a.deviceName ?? "unknown device"}`,
		`owner ${a.ownerHandle}`,
		a.statusText ? `working on: ${a.statusText}` : null,
		blocked ? "blocked" : null,
		failing ? `${failing} failed deliveries` : null,
		!a.shared ? "not participating (only you can see it)" : null,
		disarmed
			? "not wakeable until its next turn"
			: WAKE_ICON[a.presentation].name.toLowerCase(),
		a.autoResume ? "auto-resume on" : null,
	]
		.filter(Boolean)
		.join(", ")

	return (
		<div
			id={`agent-${a.id}`}
			className={cls}
			role="listitem"
			tabIndex={0}
			aria-label={summary}
			onKeyDown={e => {
				if (e.target !== e.currentTarget) return
				if (e.key === "F2" || (e.key === "Enter" && e.altKey)) {
					setDraft(a.handle)
					setEditing(true)
				} else if (e.key === "Enter") {
					setUi({ authorFilter: authorFilter === a.id ? null : a.id })
				}
			}}
		>
			<Swatch agent={a} size="lg" />
			<div style={{ minWidth: 0 }}>
				<div className="a-l1">
					{editing ? (
						<input
							className="a-rename"
							autoFocus
							value={draft}
							aria-label={`Rename @${a.handle}`}
							onChange={e => setDraft(e.target.value)}
							onBlur={() => void commit()}
							onKeyDown={e => {
								e.stopPropagation()
								if (e.key === "Enter") void commit()
								if (e.key === "Escape") setEditing(false)
							}}
							style={{ color: agentColor(a.color) }}
						/>
					) : (
						<Tip content="Double-click (or F2) to rename · click to filter the conversation">
							<button
								className="a-handle"
								style={{ color: agentColor(a.color) }}
								onClick={() =>
									setUi({ authorFilter: authorFilter === a.id ? null : a.id })
								}
								onDoubleClick={() => {
									setDraft(a.handle)
									setEditing(true)
								}}
								aria-label={`@${a.handle}: filter conversation by this agent`}
								aria-pressed={authorFilter === a.id}
							>
								@{a.handle}
							</button>
						</Tip>
					)}
					<Tip content={`${APP_LABEL[a.app]}${a.model ? ` · ${a.model}` : ""}`}>
						<span className={"app-badge " + provider}>{APP_SHORT[a.app]}</span>
					</Tip>
					<span className="caps">
						<Tip content={capTip}>
							<span
								className={
									"cap" +
									(wake.warn || disarmed ? " warn" : " ok") +
									(disarmed ? " disarmed" : "")
								}
								role="img"
								tabIndex={0}
								aria-label={capTip}
							>
								{WAKE_ICON[a.presentation].icon}
							</span>
						</Tip>
						{a.autoResume && (
							<Tip content={`${AUTO_RESUME_ICON.name}. ${AUTO_RESUME_TIP}`}>
								<span
									className="cap auto"
									role="img"
									tabIndex={0}
									aria-label={`${AUTO_RESUME_ICON.name}: ${AUTO_RESUME_TIP}`}
								>
									{AUTO_RESUME_ICON.icon}
								</span>
							</Tip>
						)}
					</span>
					<span className="dim ell" style={{ flexShrink: 1 }}>
						{a.ownerHandle}
					</span>
					<span className="grow" />
					{failing > 0 && (
						<Tip
							content={`${failing} message(s) not delivered after 2 min — is the connector running?`}
						>
							<span className="stat bad">✕{failing}</span>
						</Tip>
					)}
					{pending > 0 && (
						<Tip content={`${pending} message(s) waiting to be presented`}>
							<span className="stat pend">◷{pending}</span>
						</Tip>
					)}
					{a.stats?.p50Ms != null && (
						<Tip
							content={`Present latency p50 ${dur(a.stats.p50Ms)} · p95 ${dur(a.stats.p95Ms)} (24h)`}
						>
							<span className="stat">{dur(a.stats.p50Ms)}</span>
						</Tip>
					)}
					<Tip
						content={
							a.lastSeenAt ? `last seen ${age(a.lastSeenAt, now)} ago` : "never seen"
						}
					>
						<StateDot state={a.state} />
					</Tip>
				</div>
				<div
					className={"a-l2" + (a.statusText ? "" : " idle-text")}
					title={a.title ?? undefined}
				>
					{blocked && <span className="kind k-blocker">blocked</span>}
					<span className="ell">
						{a.statusText ?? a.title ?? (a.state === "idle" ? "Idle" : "—")}
					</span>
				</div>
				<div className="a-l3">
					{a.branch && (
						<span className="branch" title={a.cwd ?? undefined}>
							⎇ {a.branch}
						</span>
					)}
					{a.deviceName && (
						<span className="ell" style={{ flexShrink: 2 }}>
							{a.deviceName}
						</span>
					)}
					{a.forkOf && (
						<span className="marker">
							⑂ fork of{" "}
							<span style={{ color: parent ? agentColor(parent.color) : undefined }}>
								@{parent?.handle ?? "?"}
							</span>
						</span>
					)}
					{a.kind === "subagent" && (
						<span className="marker">
							↳ subagent{parent ? " of " : ""}
							{parent && (
								<span style={{ color: agentColor(parent.color) }}>@{parent.handle}</span>
							)}
						</span>
					)}
					{wake.warn && !showResume && (
						<span className="warn-mark" aria-hidden>
							{wake.label}
						</span>
					)}
					{showResume && (
						<Tip
							className="resume-tip"
							content={desktop ? DESKTOP_RESUME_TIP : AUTO_RESUME_TIP}
						>
							<label className={"share resume" + (desktop ? " disabled" : "")}>
								<input
									type="checkbox"
									className="switch"
									checked={!desktop && a.autoResume}
									aria-disabled={desktop || undefined}
									onChange={e => {
										if (!desktop) void setAutoResume(a.id, e.target.checked)
									}}
									aria-label={
										desktop
											? `Auto-resume @${a.handle}: unavailable for Codex Desktop threads`
											: `Auto-resume @${a.handle} when idle`
									}
								/>
								auto-resume
							</label>
						</Tip>
					)}
					{mine ? (
						<label
							className="share"
							title="Participating chats are visible to the team and receive messages"
						>
							<input
								type="checkbox"
								className="switch"
								checked={a.shared}
								onChange={e => void setShared(a.id, e.target.checked)}
								aria-label={`@${a.handle} participates in the room`}
							/>
							{a.shared ? "shared" : "private"}
						</label>
					) : null}
				</div>
			</div>
		</div>
	)
})
